import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { getBatchDefinitions } from 'ducks/batch-definitions/operators';
import { createParameters, deleteParameters, updateParameters } from 'ducks/parameters/operators';
import { getProject } from 'ducks/project/operators';
import { getProjects } from 'ducks/projects/operators';
import { getScheduledBatch } from 'ducks/scheduled-batch/operators';
import { getScheduledBatchHistory } from 'ducks/scheduled-batch-history/operators';
import { scheduled_batches_page_query, scheduled_batches_query } from 'ducks/scheduled-batches/constants';
import { updateSettings } from 'ducks/settings/slices';
import { Slices } from 'ducks/slices';
import { ProjectsScheduledBathesListAPI } from 'ducks/types/api-types';
import {
  ProjectsScheduledBatchesCreatePayload,
  ScheduledBatchesRunPayload,
  ScheduledBatchesUpdatePayload,
} from 'ducks/types/payload-types';
import { Query } from 'ducks/types/query-types';
import { UpdateParameters } from 'ducks/types/store-types';
import { store } from 'store';
import { Andromeda } from 'utils/axios';
import { handleError } from 'utils/errors';
import { replaceSearchQuery } from 'utils/queries';

export const getScheduledBatches = createAsyncThunk<
  ProjectsScheduledBathesListAPI[],
  { project_id: number; queries: Query }
>(`${Slices.SCHEDULED_BATCHES}/get`, async ({ project_id, queries }, { rejectWithValue }) => {
  try {
    const query = {
      ...scheduled_batches_query,
      ...queries,
    };
    const res: AxiosResponse['data'] = await Andromeda.get(
      `/projects/${project_id}/scheduled_batches/list${replaceSearchQuery(query)}`,
    );
    return res.data;
  } catch (error) {
    handleError(error);
    return rejectWithValue(error as AxiosError);
  }
});

export const getScheduledBatchesPage = async (project_id: number, queries: Query) => {
  const query = {
    ...scheduled_batches_page_query,
    ...queries,
  };

  store.dispatch(updateSettings({ loading: true }));

  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProjects()),
    store.dispatch(getProject(project_id)),
    store.dispatch(getBatchDefinitions({ project_id })),
    store.dispatch(getScheduledBatches({ project_id, queries: query })),
  ]);

  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const getScheduledBatchPage = async (project_id: number, scheduled_batch_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProject(project_id)),
    store.dispatch(getBatchDefinitions({ project_id })),
    store.dispatch(getScheduledBatch(scheduled_batch_id)),
    store.dispatch(getScheduledBatchHistory(scheduled_batch_id)),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const archiveScheduledBatch = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/scheduled_batches/${entity_id}/archive`, {});
};

export const unarchiveScheduledBatch = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/scheduled_batches/${entity_id}/unarchive`, {});
};

export const startScheduledBatch = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/scheduled_batches/${entity_id}/activate`, {});
};

export const stopScheduledBatch = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/scheduled_batches/${entity_id}/deactivate`, {});
};

export const createScheduledBatch = async (entity_id: number, data: Partial<ProjectsScheduledBatchesCreatePayload>) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/projects/${entity_id}/scheduled_batches/create`, data);
  return res.data;
};

export const updateScheduledBatch = async (entity_id: number, data: ScheduledBatchesUpdatePayload) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/scheduled_batches/${entity_id}/update`, data);
  return res.data;
};

export const updateScheduledBatchParameters = async (entity_id: number, data: UpdateParameters) => {
  const route = `/scheduled_batches/${entity_id}/parameters/by_name`;
  store.dispatch(updateSettings({ loading: true }));
  await updateParameters(route, data.update);
  await deleteParameters(route, data.delete);
  await createParameters(route, data.create);
};

export const runScheduledBatch = async (entity_id: number, data: ScheduledBatchesRunPayload) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/scheduled_batches/${entity_id}/run`, data);
  return res.data;
};
